'use client'
import NextImage from 'next/image'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import styles from './styles.module.scss'

const emptyDataURL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

export type Layout = 'responsive' | 'fill'
type ImgElementStyle = NonNullable<JSX.IntrinsicElements['img']['style']>

export interface ImageProps {
  layout?: Layout
  src?: string
  alt?: string
  placeholder?: JSX.Element | string
  threshold?: number
  rootMargin?: string
  objectFit?: ImgElementStyle['objectFit']
  objectPosition?: ImgElementStyle['objectPosition']
  loading?: 'lazy' | 'eager'
}

const Image: React.FC<ImageProps> = ({
  src,
  alt,
  placeholder,
  threshold = 0,
  rootMargin = '200px',
  layout = 'responsive',
  objectFit,
  objectPosition,
  loading = 'lazy',
}) => {
  const isPlaceholderString = useMemo(
    () => typeof placeholder === 'string',
    [placeholder],
  )

  const initialPlaceholder = useMemo(
    () => (isPlaceholderString ? placeholder : emptyDataURL),
    [placeholder, isPlaceholderString],
  ) as string

  const [imgSrc, setImgSrc] = useState(initialPlaceholder)
  const [imgStyle, setImgStyle] = useState({
    objectPosition,
    objectFit: objectFit ? objectFit : 'cover',
  })

  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading')

  const { ref: imageRef, inView: isVisible } = useInView({
    threshold,
    rootMargin,
  })

  const onLoad = useCallback(() => {
    setState('loaded')
    setImgStyle(style => ({ ...style }))
  }, [])

  const onError = useCallback(() => {
    if (isPlaceholderString || !placeholder) {
      setImgSrc(initialPlaceholder)
      setImgStyle(style => ({ ...style }))
    }

    setState('error')
  }, [initialPlaceholder, isPlaceholderString, placeholder])

  useEffect(() => {
    if ((loading === 'eager' || isVisible) && src) {
      setImgSrc(src)
    }
  }, [isVisible, loading, src])

  return (
    <div
      className={`${styles.wrapper} ${
        layout === 'fill' ? styles.fill : styles.responsive
      }`}
    >
      <NextImage
        className={styles.image}
        ref={imageRef}
        src={imgSrc}
        height={600}
        width={480}
        alt={alt || ''}
        loading={loading}
        onLoad={onLoad}
        onError={onError}
        style={imgStyle}
      />

      {placeholder && !isPlaceholderString && state !== 'loaded' && (
        <div className={styles.placeholderWrapper}>{placeholder}</div>
      )}
    </div>
  )
}

export default memo(Image)
